import { styled } from '@mui/material/styles';
import { TextField, FormControl, Select, Autocomplete, Button, DialogTitle, DialogContent, DialogActions } from '@mui/material';

/**
 * Centralized RTL styling for Material-UI components
 * This ensures consistent RTL behavior across all browsers
 */

// Enhanced RTL TextField with proper label positioning and browser compatibility
export const RTLTextField = styled(TextField)(({ theme, isRtl }) => ({
  ...(isRtl && {
    // Input text alignment
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      paddingRight: '14px',
      paddingLeft: '14px',
    },
    
    // Label positioning - Fixed for proper spacing and browser compatibility
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'top right',
      marginRight: '5px',
      // Ensure label has proper background to cover border
      backgroundColor: theme.palette.background.paper,
      paddingLeft: '4px',
      paddingRight: '4px',
      // Fix for Chrome/Firefox differences
      transform: 'translate(14px, -9px) scale(0.75)',
      '&.Mui-focused': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
    
    // Outlined input adjustments
    '& .MuiOutlinedInput-root': {
      paddingLeft: 14,
      paddingRight: 0,
      // Ensure proper border handling
      '& fieldset': {
        textAlign: 'right',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderWidth: 2,
      },
    },
    
    // Notched outline for RTL - Critical for proper label animation
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'right',
      // Ensure the notch is on the right side for RTL
      '& legend': {
        textAlign: 'right',
        marginLeft: 'auto',
        marginRight: 0,
      },
    },
    
    // Input adornments (icons)
    '& .MuiInputAdornment-root': {
      marginRight: 8,
      marginLeft: 0,
    },
    
    // Helper text
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
      direction: 'rtl',
    },
  }),
}));

// Enhanced RTL FormControl with proper Select styling
export const RTLFormControl = styled(FormControl)(({ theme, isRtl }) => ({
  ...(isRtl && {
    // Label positioning
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'top right',
      marginRight: '5px',
      backgroundColor: theme.palette.background.paper,
      paddingLeft: '4px',
      paddingRight: '4px',
      transform: 'translate(14px, -9px) scale(0.75)',
      '&.Mui-focused': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
    
    // Select input styling
    '& .MuiSelect-select': {
      paddingRight: '32px', // Space for dropdown icon
      paddingLeft: '14px',
      textAlign: 'right',
      direction: 'rtl',
    },
    
    // Dropdown icon positioning - Fixed for all browsers
    '& .MuiSelect-icon': {
      right: 'auto',
      left: '7px',
      position: 'absolute',
      pointerEvents: 'none',
      color: theme.palette.action.active,
      // Ensure icon is visible and properly positioned
      zIndex: 1,
    },
    
    // Outlined input adjustments
    '& .MuiOutlinedInput-root': {
      paddingLeft: 14,
      paddingRight: 0,
      '& fieldset': {
        textAlign: 'right',
      },
    },
    
    // Notched outline for RTL
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'right',
      '& legend': {
        textAlign: 'right',
        marginLeft: 'auto',
        marginRight: 0,
      },
    },
    
    // Helper text
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
      direction: 'rtl',
    },
  }),
}));

// Enhanced RTL Autocomplete with proper icon positioning
export const RTLAutocomplete = styled(Autocomplete)(({ theme, isRtl }) => ({
  ...(isRtl && {
    // Input styling
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      paddingRight: '14px',
      paddingLeft: '14px',
    },
    
    // Remove extra spacing from endAdornment area
    '& .MuiAutocomplete-endAdornment': {
      paddingRight: 0,
      paddingLeft: 0,
    },
    
    // Label positioning
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'top right',
      marginRight: '5px',
      backgroundColor: theme.palette.background.paper,
      paddingLeft: '4px',
      paddingRight: '4px',
      transform: 'translate(14px, -9px) scale(0.75)',
      '&.Mui-focused': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
    
    // Outlined input adjustments
    '& .MuiOutlinedInput-root': {
      paddingLeft: 14,
      paddingRight: 14,
      '& fieldset': {
        textAlign: 'right',
      },
    },
    
    // Popup indicator (dropdown arrow) - hide for multiselect
    '& .MuiAutocomplete-popupIndicator': {
      display: 'none',
    },
    
    // Clear indicator - hide completely
    '& .MuiAutocomplete-clearIndicator': {
      display: 'none !important',
    },
    
    // Notched outline for RTL
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'right',
      '& legend': {
        textAlign: 'right',
        marginLeft: 'auto',
        marginRight: 0,
      },
    },
    
    // Helper text
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
      direction: 'rtl',
    },
  }),
}));

// RTL Button
export const RTLButton = styled(Button)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
    '& .MuiButton-startIcon': {
      marginLeft: 8,
      marginRight: 0,
    },
    '& .MuiButton-endIcon': {
      marginRight: 8,
      marginLeft: 0,
    },
  }),
}));

// RTL Dialog components
export const RTLDialogTitle = styled(DialogTitle)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

export const RTLDialogContent = styled(DialogContent)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

export const RTLDialogActions = styled(DialogActions)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

// Additional RTL utilities
export const getRTLProps = (isRtl, component = 'textfield') => {
  if (!isRtl) return {};
  
  const baseProps = {
    isRtl,
  };
  
  switch (component) {
    case 'select':
      return {
        ...baseProps,
        MenuProps: {
          PaperProps: {
            sx: {
              direction: 'rtl',
              '& .MuiMenuItem-root': {
                justifyContent: 'flex-end',
                textAlign: 'right',
                direction: 'rtl',
              },
            },
          },
        },
      };
    case 'autocomplete':
      return {
        ...baseProps,
        renderOption: (props, option) => (
          <li {...props} style={{ textAlign: 'right', direction: 'rtl' }}>
            {option.label}
          </li>
        ),
      };
    default:
      return baseProps;
  }
};
